/**
 * FlahaSOIL authentication layout.
 *
 * Auth forms sit outside the app chrome. Document title / robots are owned
 * by DocumentMeta (path-based noindex for all auth utility routes).
 */
import { Box, Container, Stack, Typography } from "@mui/material";
import { Link as RouterLink, Outlet, useLocation } from "react-router-dom";

import {
	BRAND_FOOTER,
	BRAND_HIERARCHY,
	BRAND_TAGLINE,
	BRAND_WORDMARK,
} from "../theme/brand";
import { usePageHeader } from "./PageHeaderContext";
import { FlahaLogo } from "./components/FlahaLogo";
import { GlassSurface } from "./components/GlassSurface";

function authPageTitle(pathname: string): string {
	if (pathname.startsWith("/register")) return "Create account";
	if (pathname.startsWith("/forgot-password")) return "Forgot password";
	if (pathname.startsWith("/reset-password")) return "Reset password";
	return "Sign in";
}

export function AuthLayout() {
	const { pathname } = useLocation();
	usePageHeader({
		title: authPageTitle(pathname),
		subtitle: BRAND_TAGLINE,
	});

	return (
		<Box
			component="main"
			id="main-content"
			sx={{
				minHeight: "100vh",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				py: { xs: 3, sm: 5 },
			}}
		>
			<Container maxWidth="xs">
				<Stack spacing={3} alignItems="center">
					<Stack spacing={1.25} alignItems="center">
						<RouterLink
							to="/"
							aria-label={`${BRAND_WORDMARK} home`}
							style={{ color: "inherit", textDecoration: "none" }}
						>
							<FlahaLogo size={48} variant="family" />
						</RouterLink>
						<Typography
							variant="caption"
							sx={{
								color: "workspace.textMuted",
								letterSpacing: "0.08em",
								textTransform: "uppercase",
								textAlign: "center",
							}}
						>
							{BRAND_HIERARCHY}
						</Typography>
						<Typography
							variant="body2"
							sx={{
								color: "workspace.textSecondary",
								textAlign: "center",
							}}
						>
							{BRAND_TAGLINE}
						</Typography>
					</Stack>

					<GlassSurface
						data-testid="auth-glass-surface"
						intensity="strong"
						depth="floating"
						sx={{
							p: { xs: 2.5, sm: 3.5 },
							width: "100%",
							color: "workspace.textPrimary",
						}}
					>
						<Outlet />
					</GlassSurface>

					<Typography
						variant="caption"
						sx={{ color: "workspace.textMuted", textAlign: "center" }}
					>
						{BRAND_FOOTER}
					</Typography>
				</Stack>
			</Container>
		</Box>
	);
}
